/**
 * quiz 展示层止血(宪法:数据修复属 Stage 3,此处只兜渲染)。
 *
 * 实测题库原料(faq/organized_pages/06_json)两类脏形:
 *   1. answer 粘连:「A 或 B\n【解析】此牌面并没有…」——答案字母后直接粘了整段解析;
 *      同文件的 explanation 字段往往就是粘段的副本(双写)。
 *   2. LaTeX 残留(约 17%):\(C_{4}^2=\frac{4\times3}{2}=6\) 一类,
 *      实测命令词表:\boldsymbol \frac \times \approx \mathrm \div \text。
 *
 * 两个函数都是幂等纯函数:干净文本原样通过,不引入任何新依赖(无 KaTeX)。
 */

const GLUE_MARKER = "【解析】";

/** 实测 LaTeX 命令 → 可读纯文本;未登记的命令直接丢反斜杠 */
const TEX_SYMBOLS: Record<string, string> = {
  times: "×",
  cdot: "·",
  div: "÷",
  approx: "≈",
  ge: "≥",
  geq: "≥",
  le: "≤",
  leq: "≤",
  ne: "≠",
  infty: "∞",
};

/**
 * 把 LaTeX 残留转成可读纯文本:
 * $\frac{1}{2}$ / \(\frac{1}{2}\) → 1/2,\% → %,上下标与定界符脱落。
 */
export function stripLatex(raw: string): string {
  let s = raw;
  // 数学定界符:$…$ 与 \(…\) → 保留内容
  s = s.replace(/\$([^$]*)\$/g, "$1");
  s = s.replace(/\\\(([\s\S]*?)\\\)/g, "$1");
  // \frac{a}{b} → a/b
  s = s.replace(/\\frac\{([^{}]*)\}\{([^{}]*)\}/g, "$1/$2");
  // 包裹型命令 \boldsymbol{…} \mathrm{…} \text{…} → 脱壳留内容
  s = s.replace(/\\(?:boldsymbol|mathrm|operatorname|text)\{([^{}]*)\}/g, "$1");
  // 转义字符 \% \& \# \_ \{ \} → 字面量
  s = s.replace(/\\([%&#_{}])/g, "$1");
  // 其余命名命令 → 符号表,未登记则丢弃
  s = s.replace(/\\([a-zA-Z]+)/g, (_m, name: string) => TEX_SYMBOLS[name] ?? "");
  // 间距命令 \, \; \: \! \| → 空格
  s = s.replace(/\\[,;:!|]/g, " ");
  // LaTeX 换行 \\ → 空格
  s = s.replace(/\\\\/g, " ");
  // 花括号与上下标标记脱落,保留内容:C_{4}^2 → C42
  s = s.replace(/[{}]/g, "");
  s = s.replace(/[_^]+/g, "");
  return s.trim();
}

/**
 * 拆开 answer 里粘连的「答案\n【解析】…」:
 * 前段作答案,后段作解析;若 explanation 字段已含同一内容(题库双写)则去重。
 */
export function splitGluedAnswer(
  answer: string,
  explanation?: string | null,
): { answer: string; explanation: string | null } {
  const existing = (explanation ?? "").trim();
  const idx = answer.indexOf(GLUE_MARKER);
  if (idx < 0) return { answer, explanation: existing || null };

  const head = answer.slice(0, idx).trim();
  const tail = answer.slice(idx + GLUE_MARKER.length).trim();
  if (!tail) return { answer: head, explanation: existing || null };

  if (!existing) return { answer: head, explanation: tail };
  // 题库双写:explanation 与粘段互为子串 → 只留较长者,不同屏两遍
  if (existing === tail || existing.includes(tail)) {
    return { answer: head, explanation: existing };
  }
  if (tail.includes(existing)) return { answer: head, explanation: tail };
  return { answer: head, explanation: `${existing}\n${tail}` };
}
